import React, { useState, useEffect } from 'react';
import { Mail, Save, Trash2, Send, AlertCircle, CheckCircle, Power, Bell } from 'lucide-react';
import api from '@/api/api';

const EmailSettings = () => {
  const [config, setConfig] = useState({
    sender_email: '',
    sender_name: '',
    service_type: 'default',
    resend_api_key: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    use_tls: true,
    is_enabled: true,
    enable_admission_notification: true,
    enable_fee_notification: true,
    enable_attendance_notification: true,
    enable_result_notification: true
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/email-settings/');
      if (response.data.configured) {
        setConfig({
          ...config,
          sender_email: response.data.sender_email,
          sender_name: response.data.sender_name,
          service_type: response.data.service_type,
          is_enabled: response.data.is_enabled !== false,
          enable_admission_notification: response.data.enable_admission_notification !== false,
          enable_fee_notification: response.data.enable_fee_notification !== false,
          enable_attendance_notification: response.data.enable_attendance_notification !== false,
          enable_result_notification: response.data.enable_result_notification !== false
        });
        setIsConfigured(true);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await api.post('/email-settings/', config);
      setMessage({ type: 'success', text: 'Email settings saved successfully!' });
      setIsConfigured(true);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save settings' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete email settings? This will revert to default configuration.')) {
      return;
    }

    setSaving(true);
    try {
      await api.delete('/email-settings/');
      setConfig({
        sender_email: '',
        sender_name: '',
        service_type: 'default',
        resend_api_key: '',
        smtp_host: '',
        smtp_port: 587,
        smtp_username: '',
        smtp_password: '',
        use_tls: true
      });
      setIsConfigured(false);
      setMessage({ type: 'success', text: 'Email settings deleted. Using default configuration.' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async (e) => {
    e.preventDefault();
    if (!testEmail) {
      setMessage({ type: 'error', text: 'Please enter a test email address' });
      return;
    }

    setTesting(true);
    setMessage({ type: '', text: '' });

    try {
      await api.post(`/email-settings/test?test_email=${testEmail}`);
      setMessage({ type: 'success', text: `Test email sent successfully to ${testEmail}!` });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to send test email' 
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Mail className="h-8 w-8" />
          Email Settings
        </h1>
        <p className="text-gray-600 mt-2">
          Configure email settings for sending fee reminders and notifications
        </p>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {!isConfigured && (
        <div className="mb-6 p-4 bg-blue-50 text-blue-800 rounded-lg border border-blue-200">
          <p className="font-medium">Using Default Configuration</p>
          <p className="text-sm mt-1">
            Emails will be sent using our default service. Configure your own settings below for custom sender email.
          </p>
        </div>
      )}

      {/* Enable/Disable Email Service */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Power className={`h-6 w-6 ${config.is_enabled ? 'text-green-500' : 'text-gray-400'}`} />
            <div>
              <h3 className="font-semibold text-gray-900">Email Service</h3>
              <p className="text-sm text-gray-500">Enable or disable all email notifications</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.is_enabled}
              onChange={(e) => setConfig({...config, is_enabled: e.target.checked})}
              className="sr-only peer"
            />
            <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500"></div>
            <span className="ml-3 text-sm font-medium text-gray-700">{config.is_enabled ? 'ON' : 'OFF'}</span>
          </label>
        </div>
      </div>

      {/* Notification Types */}
      {config.is_enabled && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Email Notification Types
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
              <span className="text-sm text-gray-700">Admission Notifications</span>
              <input
                type="checkbox"
                checked={config.enable_admission_notification}
                onChange={(e) => setConfig({...config, enable_admission_notification: e.target.checked})}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
            </label>
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
              <span className="text-sm text-gray-700">Fee/Payment Notifications</span>
              <input
                type="checkbox"
                checked={config.enable_fee_notification}
                onChange={(e) => setConfig({...config, enable_fee_notification: e.target.checked})}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
            </label>
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
              <span className="text-sm text-gray-700">Attendance Alerts</span>
              <input
                type="checkbox"
                checked={config.enable_attendance_notification}
                onChange={(e) => setConfig({...config, enable_attendance_notification: e.target.checked})}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
            </label>
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
              <span className="text-sm text-gray-700">Result Notifications</span>
              <input
                type="checkbox"
                checked={config.enable_result_notification}
                onChange={(e) => setConfig({...config, enable_result_notification: e.target.checked})}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
            </label>
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className="bg-white rounded-lg shadow p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sender Email *
            </label>
            <input
              type="email"
              value={config.sender_email}
              onChange={(e) => setConfig({...config, sender_email: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="fees@yourcollegename.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sender Name (College Name) *
            </label>
            <input
              type="text"
              value={config.sender_name}
              onChange={(e) => setConfig({...config, sender_name: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="ABC College"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Service Type *
          </label>
          <select
            value={config.service_type}
            onChange={(e) => setConfig({...config, service_type: e.target.value})}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="default">Default (Emergent Resend)</option>
            <option value="resend">Custom Resend API</option>
            <option value="smtp">SMTP (Gmail, Outlook, etc.)</option>
          </select>
        </div>

        {config.service_type === 'resend' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Resend API Key *
            </label>
            <input
              type="password"
              value={config.resend_api_key}
              onChange={(e) => setConfig({...config, resend_api_key: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="re_..."
              required={config.service_type === 'resend'}
            />
            <p className="text-sm text-gray-500 mt-1">
              Get your API key from <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">resend.com</a>
            </p>
          </div>
        )}

        {config.service_type === 'smtp' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Host *
              </label>
              <input
                type="text"
                value={config.smtp_host}
                onChange={(e) => setConfig({...config, smtp_host: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="smtp.gmail.com"
                required={config.service_type === 'smtp'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Port *
              </label>
              <input
                type="number"
                value={config.smtp_port}
                onChange={(e) => setConfig({...config, smtp_port: parseInt(e.target.value)})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="587"
                required={config.service_type === 'smtp'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Username *
              </label>
              <input
                type="text"
                value={config.smtp_username}
                onChange={(e) => setConfig({...config, smtp_username: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="your@email.com"
                required={config.service_type === 'smtp'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Password *
              </label>
              <input
                type="password"
                value={config.smtp_password}
                onChange={(e) => setConfig({...config, smtp_password: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="********"
                required={config.service_type === 'smtp'}
              />
            </div>

            <div className="col-span-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={config.use_tls}
                  onChange={(e) => setConfig({...config, use_tls: e.target.checked})}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Use TLS</span>
              </label>
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
          >
            <Save className="h-5 w-5" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>

          {isConfigured && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400"
            >
              <Trash2 className="h-5 w-5" />
              Delete Settings
            </button>
          )}
        </div>
      </form>

      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Test Email Configuration</h2>
        <form onSubmit={handleTestEmail} className="flex gap-3">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Enter test email address"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="submit"
            disabled={testing || !isConfigured}
            className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
          >
            <Send className="h-5 w-5" />
            {testing ? 'Sending...' : 'Send Test Email'}
          </button>
        </form>
        {!isConfigured && (
          <p className="text-sm text-gray-500 mt-2">
            Save your email settings first before sending a test email.
          </p>
        )}
      </div>
    </div>
  );
};

export default EmailSettings;
